/*
 *   File : tooltip.js
 *   Author : https://evoqins.com
 *   Description : tooltip component
 *   Integrations : NA
 *   Version : 1.0.0
 */

import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

export default function CustomTooltip({
  content,
  placement = "bottom-right", // override to unified fixed direction
  children,
  delay = 0,
  customClass = "",
  id,
}) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const showTimeout = useRef();
  const hideTimeout = useRef();

  // compute fixed bottom-right position relative to trigger
  const updatePosition = () => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip) return;

    const tRect = trigger.getBoundingClientRect();

    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;

    // "bottom-right" = tooltip appears bottom + right aligned to trigger
    const top = tRect.bottom + scrollY; // bottom gap
    const left = tRect.right + scrollX; // right gap

    setCoords({ top, left });
  };

  useEffect(() => {
    if (visible) updatePosition();
    const handle = () => visible && updatePosition();
    window.addEventListener("scroll", handle);
    window.addEventListener("resize", handle);
    return () => {
      window.removeEventListener("scroll", handle);
      window.removeEventListener("resize", handle);
    };
  }, [visible]);

  useEffect(() => {
    return () => {
      clearTimeout(showTimeout.current);
      clearTimeout(hideTimeout.current);
    };
  }, []);

  const show = () => {
    clearTimeout(hideTimeout.current);
    showTimeout.current = setTimeout(() => setVisible(true), delay);
  };

  const hide = () => {
    clearTimeout(showTimeout.current);
    hideTimeout.current = setTimeout(() => setVisible(false), delay);
  };

  const child = React.Children.only(children);
  const triggerProps = {
    ref: triggerRef,
    onMouseEnter: (e) => {
      show();
      child.props.onMouseEnter && child.props.onMouseEnter(e);
    },
    onMouseLeave: (e) => {
      hide();
      child.props.onMouseLeave && child.props.onMouseLeave(e);
    },
    onFocus: (e) => {
      show();
      child.props.onFocus && child.props.onFocus(e);
    },
    onBlur: (e) => {
      hide();
      child.props.onBlur && child.props.onBlur(e);
    },
    "aria-describedby": id || "custom-tooltip",
  };

  const tooltipNode = (
    <div
      ref={tooltipRef}
      role="tooltip"
      id={id || "custom-tooltip"}
      className={`pointer-events-none z-50 transform transition-opacity duration-150 ${
        visible ? "opacity-100" : "opacity-0"
      } ${customClass} e-cursor-pointer`}
      style={{ position: "absolute", top: coords.top, left: coords.left }}
    >
      <div
        className="rounded-8 p-3 rounded-28 shadow-lg bg-white bg-opacity-43 e-bg-blur-4 text-eerie-black fs-12"
      >
        {content}
      </div>
    </div>
  );

  return (
    <>
      {React.cloneElement(child, triggerProps)}
      {createPortal(tooltipNode, document.body)}
    </>
  );
}

/* -----------------------
  <CustomTooltip content={<span>This top tooltip is themed via CSS variables.</span>} placement="top">
    <button type="button" className="btn btn-secondary">
      Custom tooltip
    </button>
  </CustomTooltip>

-------------------------*/
